import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';

import {
  AREA,
  COLUMN_GRAPH,
  HIGH_BAR,
  LINE_GRAPH,
  LINE_HIGH_CHART,
  PIE,
  VERTICAL_COLUMN_GRAPH,
} from '../../Constants/WidgetTypes';
import GraphOptionsBuilder from '../../Lib/GraphOptionsBuilder';
import WidgetUtils from '../../Lib/WidgetUtils';

export const ScanWidget = memo(function ScanWidget({ data }) {
  if (!data?.length) return null;

  const splitString = data.split('\n');
  return (
    <div className="p-2">
      {splitString.map((line, i) => (
        <p className="mb-0 text-nowrap" key={i}>
          {line}
        </p>
      ))}
    </div>
  );
});

ScanWidget.propTypes = { data: PropTypes.string };

const graphTypeMap = new Map([
  [AREA, AREA],
  [COLUMN_GRAPH, 'bar'],
  [HIGH_BAR, 'bar'],
  [LINE_GRAPH, 'line'],
  [LINE_HIGH_CHART, 'line'],
  [PIE, PIE],
  [VERTICAL_COLUMN_GRAPH, 'bar'],
]);

export const GraphWidget = memo(function GraphWidget({
  alertVisibility,
  fetchedData,
  graphIndex,
  graphWidgetIDs,
  renderNextGraph,
  series,
  settings,
  signalUnitsMap,
  type,
}) {
  const widgetUtils = useMemo(() => new WidgetUtils(), []);

  const { lines, title } = widgetUtils.getWidgetAttributes(type, settings);
  const yMinMax = alertVisibility ? {} : settings;
  const options = buildGraphOptions({
    fetchedData,
    graphIndex,
    graphWidgetIDs,
    lines,
    renderNextGraph,
    signalUnitsMap,
    title,
    type,
    yMinMax,
  });

  const seriesForType = useMemo(() => {
    switch (type) {
      case COLUMN_GRAPH:
      case HIGH_BAR:
      case VERTICAL_COLUMN_GRAPH:
        return series.reduce(
          (acc, s) => {
            const data = acc[0].data.concat(...s.data);
            return [{ data }];
          },
          [{ data: [] }]
        );
      default:
        return series;
    }
  }, [series, type]);

  return (
    <Chart
      height="100%"
      options={options}
      series={seriesForType}
      type={graphTypeMap.get(type)}
      width="100%"
    />
  );
});

GraphWidget.propTypes = {
  alertVisibility: PropTypes.bool,
  fetchedData: PropTypes.object,
  graphIndex: PropTypes.number,
  graphWidgetIDs: PropTypes.arrayOf(PropTypes.string),
  renderNextGraph: PropTypes.func,
  series: PropTypes.array,
  settings: PropTypes.object,
  signalUnitsMap: PropTypes.object,
  type: PropTypes.string,
};

function buildGraphOptions({
  fetchedData,
  graphIndex,
  graphWidgetIDs,
  lines,
  renderNextGraph,
  signalUnitsMap,
  title,
  type,
  yMinMax: { yMax, yMin },
}) {
  const options = new GraphOptionsBuilder(
    title,
    graphWidgetIDs,
    graphIndex,
    renderNextGraph
  );

  switch (type) {
    case AREA:
    case LINE_HIGH_CHART:
    case LINE_GRAPH:
      return options.addLineAndAreaOptions({ yMax, yMin }, signalUnitsMap);
    case COLUMN_GRAPH:
    case HIGH_BAR:
      return options.addBarOptions(lines, fetchedData, signalUnitsMap, {
        yMax,
        yMin,
      });
    case PIE:
      return options.addPieChartOptions(lines, fetchedData, signalUnitsMap);
    case VERTICAL_COLUMN_GRAPH:
      return options.addVerticalBarOptions(lines, fetchedData, signalUnitsMap, {
        yMax,
        yMin,
      });
  }
}
